import { useContext, useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  toast,
  Modal,
  ModalContext,
  ViewerContext,
  Spinner,
  Icon,
  useBackendErrorMessage,
} from "dash-ui";
import {
  useChangeCurrentProductSubscriptionMutation,
  useScheduleDowngradeMutation,
  GetCurrentAccountProductSubscriptionDocument,
  GetAppBasicInfoDocument,
} from "gql/generated.graphql";
import { RecurringTypesEnum, SubscriptionProductNamesEnum } from "dash-common";
import { getDisplayName } from "common/components/SubscriptionComponents/SubscriptionPlanCardV3/SubscriptionPlanCardV3.helpers";
import { getSubscriptionTheme } from "common/components/SubscriptionComponents/SubscriptionComponents.constants";
import { getPostPurchaseRedirectPath } from "common/helpers";
import { AnalyticsEvent, trackEvent } from "shared/analytics";

// Sub-components
import ModalHeader from "./ModalHeader/ModalHeader";
import PlanInfoCard from "./PlanInfoCard/PlanInfoCard";
import PricingBreakdown from "./PricingBreakdown/PricingBreakdown";
import TotalDueSection from "./TotalDueSection/TotalDueSection";
import PaymentMethodSection from "./PaymentMethodSection/PaymentMethodSection";
import PromoCodeInput from "./PromoCodeInput/PromoCodeInput";
import ModalActions from "./ModalActions/ModalActions";

// Hooks and helpers
import {
  useSubscriptionChangeData,
  useSubscriptionPricing,
} from "./SubscriptionChangeConfirmationModalV2.hooks";
import {
  SubscriptionChangeMode,
  SubscriptionChangePayload,
  TrialUpgradeDetails,
} from "./SubscriptionChangeConfirmationModalV2.interfaces";
import { getTrialEndDate } from "./SubscriptionChangeConfirmationModalV2.helpers";

const SubscriptionChangeConfirmationModalV2 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("pages", {
    keyPrefix: "profilePage.billing.trialUpgrade",
  });
  const { t: tc } = useTranslation(["pages", "common"]);
  const translate = useBackendErrorMessage();
  const { account } = useContext(ViewerContext);
  const { isOpenCurrent, payload, toggleModal } = useContext(ModalContext);
  const isOpen = !!isOpenCurrent["SubscriptionChangeConfirmationModalV2"];
  const { subscription, setSubscription } = useContext(ViewerContext);

  const closeModal = () => {
    toggleModal(false, "SubscriptionChangeConfirmationModalV2");
  };

  const typedPayload = payload as SubscriptionChangePayload | undefined;
  const newProduct = typedPayload?.newProduct;
  const mode: SubscriptionChangeMode = typedPayload?.mode || "upgrade";

  // DSH-837: leaving the API plan for a standard plan deactivates the account's
  // API credential (the backend does this on the change — see the DSH-898
  // refetch below). Warn the user and require an explicit acknowledgement before
  // they can confirm. Derived from ViewerContext instead of the payload so we
  // don't have to thread a "leaving API" flag through every caller of the modal.
  const isLeavingApiPlan =
    subscription?.subscriptionProduct?.name ===
      SubscriptionProductNamesEnum.API &&
    newProduct?.name !== SubscriptionProductNamesEnum.API;

  const [promoCode, setPromoCode] = useState<string | undefined>();
  const [promoCodeInput, setPromoCodeInput] = useState<string>("");
  const [apiWarningAcknowledged, setApiWarningAcknowledged] = useState(false);

  // Custom hooks for data fetching and pricing
  const { invoice, loadingInvoice, error, defaultPaymentMethod } =
    useSubscriptionChangeData({
      isOpen,
      newProduct: newProduct || null,
      mode,
      promoCode,
    });

  const pricing = useSubscriptionPricing({
    newProduct: newProduct || null,
    invoice,
  });

  // The preview is the source of truth: if it still asks for money today, the
  // backend is not starting a trial for this customer, so don't promise one.
  const trialDays = newProduct?.trial || 0;
  const isTrialUpgrade =
    !!typedPayload?.withTrial &&
    mode === "upgrade" &&
    trialDays > 0 &&
    (invoice?.amountDue ?? 0) <= 0;
  const trial = useMemo<TrialUpgradeDetails | undefined>(
    () =>
      isTrialUpgrade
        ? { days: trialDays, endDate: getTrialEndDate(trialDays) }
        : undefined,
    [isTrialUpgrade, trialDays, isOpen]
  );

  const [
    changeSubscriptionMutation,
    { loading: isChangeProductSubscriptionLoading },
  ] = useChangeCurrentProductSubscriptionMutation();

  const [scheduleDowngradeMutation, { loading: isDowngradeLoading }] =
    useScheduleDowngradeMutation();

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setPromoCode(undefined);
      setPromoCodeInput("");
      setApiWarningAcknowledged(false);
    }
  }, [isOpen]);

  const onChangeSuccessful = (previousProductName?: string) => {
    trackEvent(AnalyticsEvent.SUBSCRIPTION_CHANGED, {
      mode,
      from_product: previousProductName,
      to_product: newProduct?.name,
    });

    closeModal();
    toast.success(
      trial
        ? t("successToast")
        : mode === "upgrade"
        ? tc("pages:profilePage.billing.invoicePreview.upgraded")
        : tc("pages:profilePage.billing.invoicePreview.downgradeScheduled")
    );

    // DSH-853: land the buyer on their new plan's page. Only for an immediate
    // change ("downgrade" is scheduled for the period end, so the plan is not
    // theirs yet) and only when the product actually changed — switching
    // cadence within the same plan (e.g. Pro monthly -> Pro yearly) also comes
    // through here as an "upgrade" and must not yank the user off their page.
    if (mode !== "upgrade" || newProduct?.name === previousProductName) return;

    const redirectPath = getPostPurchaseRedirectPath(newProduct?.name);
    if (redirectPath) navigate(redirectPath);
  };

  const handleSubmit = async () => {
    if (!newProduct?._id || !newProduct?.stripePriceId) return;

    // Read before the mutation: once it resolves ViewerContext already holds
    // the NEW product (we set it below), so this would always compare equal.
    const previousProductName = subscription?.subscriptionProduct?.name;

    try {
      if (mode === "upgrade") {
        const result = await changeSubscriptionMutation({
          variables: {
            input: {
              subscriptionProductId: newProduct._id,
              stripeCouponId: promoCode,
            },
          },
          refetchQueries: [
            GetCurrentAccountProductSubscriptionDocument,
            // DSH-898: also refresh the API-credential query so switching away
            // from the API plan immediately hides the API card. The backend
            // deactivates the credential on the change; without this refetch the
            // stale isActive:true cache keeps showApiPlan true until a reload.
            GetAppBasicInfoDocument,
          ],
          awaitRefetchQueries: true,
        });

        // DSH-853: put the subscription the mutation just returned into
        // ViewerContext before we navigate. The refetch above only warms the
        // Apollo cache — ViewerContext is React state that otherwise waits for
        // the websocket push, and the page we send the buyer to gates its
        // content on it, so they would land on the "upgrade to unlock" landing
        // they just paid to get past. Same move as CancelScheduledDowngradeModal.
        const updatedSubscription =
          result.data?.changeCurrentProductSubscription?.productSubscription;

        if (updatedSubscription && setSubscription) {
          setSubscription({ ...subscription, ...updatedSubscription });
        }
      } else {
        await scheduleDowngradeMutation({
          variables: {
            input: {
              newStripePriceId: newProduct.stripePriceId,
            },
          },
          refetchQueries: [GetCurrentAccountProductSubscriptionDocument],
          awaitRefetchQueries: true,
        });
      }

      onChangeSuccessful(previousProductName);
    } catch (error: any) {
      toast.error(
        translate(
          error,
          tc("pages:profilePage.billing.changeConfirm.changeError") || ""
        )
      );
    }
  };

  const handleApplyPromoCode = () => {
    if (!promoCodeInput.trim()) return;
    setPromoCode(promoCodeInput);
  };

  // Memoized calculations
  const productTheme = useMemo(
    () =>
      newProduct
        ? getSubscriptionTheme(newProduct.name)
        : getSubscriptionTheme("Basic"),
    [newProduct]
  );

  const displayName = useMemo(
    () => (newProduct ? getDisplayName(newProduct.name) : ""),
    [newProduct]
  );

  const currentPlanName = subscription?.subscriptionProduct?.name
    ? getDisplayName(subscription.subscriptionProduct.name)
    : undefined;

  const isPro = useMemo(
    () =>
      newProduct?.name === SubscriptionProductNamesEnum.PRO &&
      newProduct?.recurringType !== RecurringTypesEnum.SIXMONTH,
    [newProduct?.name, newProduct?.recurringType]
  );

  // Early return if modal is not properly initialized
  if (!isOpen || !newProduct) return null;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={closeModal}
      className="subscription-change-modal"
      topMargin={trial ? 12 : undefined}
      maxWidth="500px"
      w="500px"
      h="auto"
    >
      <div className="flex flex-col w-full">
        <ModalHeader mode={mode} isTrial={!!trial} onClose={closeModal} />

        {loadingInvoice && (
          <div className="flex justify-center items-center h-48">
            <Spinner />
          </div>
        )}

        {error && !loadingInvoice && (
          <div className="flex text-sm my-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700">
            <Icon
              name={"warningOutlined"}
              size={28}
              className="flex-shrink-0 mr-2"
            />
            {tc("pages:profilePage.billing.changeConfirm.fetchError")}
          </div>
        )}

        {!loadingInvoice && !error && (
          <>
            <PlanInfoCard
              newProduct={newProduct}
              displayName={displayName}
              productTheme={productTheme}
              mode={mode}
              isPro={isPro}
              currentPeriodEndAt={subscription?.currentPeriodEndAt}
              trial={trial}
              currentPlanName={currentPlanName}
            />

            <PricingBreakdown
              displayName={displayName}
              recurringType={newProduct.recurringType}
              planPrice={trial ? newProduct.price : pricing.planPrice}
              creditAmount={pricing.creditAmount}
              discountAmount={pricing.discountAmount}
              coupon={pricing.coupon}
              trial={trial}
            />

            <TotalDueSection
              mode={mode}
              totalDue={trial ? 0 : pricing.totalDue}
              compact={!!trial}
            />

            <PaymentMethodSection
              paymentMethod={defaultPaymentMethod}
              stripeCustomerId={account?.stripeCustomerId}
              compact={!!trial}
            />

            {mode === "upgrade" && (
              <PromoCodeInput
                value={promoCodeInput}
                onChange={setPromoCodeInput}
                onApply={handleApplyPromoCode}
                loading={loadingInvoice}
              />
            )}

            {/* DSH-837: explicit warning + acknowledgement when leaving the API
                plan. Confirm stays disabled until the checkbox is checked. */}
            {isLeavingApiPlan && (
              <div className="my-4">
                <div className="flex text-sm p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700">
                  <Icon
                    name={"warningOutlined"}
                    size={28}
                    className="flex-shrink-0 mr-2"
                  />
                  <span>
                    {tc("pages:profilePage.billing.changeConfirm.apiWarning")}
                  </span>
                </div>
                <label className="flex items-start gap-2 mt-3 text-sm text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={apiWarningAcknowledged}
                    onChange={(e) =>
                      setApiWarningAcknowledged(e.target.checked)
                    }
                    className="mt-0.5 flex-shrink-0"
                  />
                  <span>
                    {tc(
                      "pages:profilePage.billing.changeConfirm.apiAcknowledge"
                    )}
                  </span>
                </label>
              </div>
            )}

            <ModalActions
              mode={mode}
              totalDue={pricing.totalDue}
              hasPaymentMethod={!!defaultPaymentMethod}
              loading={isChangeProductSubscriptionLoading || isDowngradeLoading}
              loadingInvoice={loadingInvoice}
              currentPeriodEndAt={subscription?.currentPeriodEndAt}
              trial={trial}
              confirmDisabled={isLeavingApiPlan && !apiWarningAcknowledged}
              onCancel={closeModal}
              onConfirm={handleSubmit}
            />
          </>
        )}
      </div>
    </Modal>
  );
};

export default SubscriptionChangeConfirmationModalV2;
